import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl, externalLinkAttrs } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [guidesDropdownOpen, setGuidesDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);
  const [mobileGuidesOpen, setMobileGuidesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const mobileDropdownRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const guidesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setDropdownOpen(true);
  };
  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => { setDropdownOpen(false); }, 200);
  };

  const handleGuidesEnter = () => {
    if (guidesTimerRef.current) { clearTimeout(guidesTimerRef.current); guidesTimerRef.current = null; }
    setGuidesDropdownOpen(true);
  };
  const handleGuidesLeave = () => {
    guidesTimerRef.current = setTimeout(() => { setGuidesDropdownOpen(false); }, 200);
  };

  const isOnHero = !scrolled;

  const navTextColor = isOnHero ? 'text-white' : 'text-foreground-700';
  const navTextHover = isOnHero ? 'hover:text-white/80' : 'hover:text-foreground-950';
  const navBorder = isOnHero ? 'border-white/30 hover:border-white/60' : 'border-foreground-200/50 hover:border-foreground-400/50';
  const logoColor = isOnHero ? 'text-white' : 'text-foreground-950';
  const accentColor = isOnHero ? 'text-white' : 'text-accent-500';

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-black/20 backdrop-blur-sm'
      }`}
    >
      <div className="w-full px-6 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-16">
          <Link to="/" className="flex items-center gap-2 group">
            <span className={`text-lg md:text-xl font-heading font-bold tracking-tight ${logoColor} transition-colors duration-300`}>
              SugarGoo<span className={accentColor}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${navTextColor} ${navTextHover}`}
            >
              Home
            </Link>

            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full border text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextColor} ${navBorder} ${navTextHover}`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div
                  className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-background-50 border border-background-200/80 rounded-lg py-2 w-72 shadow-sm"
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={handleMouseLeave}
                >
                  <div className="grid grid-cols-1 max-h-[400px] overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        {...externalLinkAttrs}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap"
                      >
                        <span className="w-5 h-5 flex items-center justify-center">
                          <i className={`${cat.icon} text-base`}></i>
                        </span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div
              ref={guidesRef}
              className="relative"
              onMouseEnter={handleGuidesEnter}
              onMouseLeave={handleGuidesLeave}
            >
              <button
                className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${navTextColor} ${navTextHover}`}
              >
                <span>Shopping Guides</span>
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${guidesDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {guidesDropdownOpen && (
                <div
                  className="absolute top-full mt-2 left-0 bg-background-50 border border-background-200/80 rounded-lg py-2 w-64 shadow-sm"
                  onMouseEnter={handleGuidesEnter}
                  onMouseLeave={handleGuidesLeave}
                >
                  <Link to="/article/best-sugargoo-spreadsheet-2026" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Complete Guide 2026</Link>
                  <Link to="/article/how-to-use-sugargoo-spreadsheet" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">How To Use Spreadsheets</Link>
                  <Link to="/article/sugargoo-spreadsheet-shoes-guide" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Shoes Buying Guide</Link>
                  <Link to="/article/sugargoo-spreadsheet-budget-finds" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Budget Finds Under $20</Link>
                  <Link to="/article/sugargoo-shipping-guide-beginners" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Shipping Guide</Link>
                  <Link to="/article/sugargoo-qc-guide-explained" className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">QC Inspection Guide</Link>
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              {...externalLinkAttrs}
              className={`text-sm font-medium transition-colors whitespace-nowrap ${navTextColor} ${navTextHover}`}
            >
              Main Website
            </a>

            <a
              href={mainSiteUrl}
              {...externalLinkAttrs}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors whitespace-nowrap ${
                isOnHero
                  ? 'bg-white text-foreground-950 hover:bg-white/90'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              Buy It Now
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${navTextColor}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className={`md:hidden pb-4 border-t mt-2 pt-4 ${isOnHero ? 'border-white/20' : 'border-background-200/50'}`}>
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`px-3 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${navTextColor} ${isOnHero ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
              >
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-home-line"></i></span>
                  Home
                </span>
              </Link>

              <div ref={mobileDropdownRef}>
                <button
                  className={`flex items-center justify-between w-full px-3 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${navTextColor} ${isOnHero ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
                  onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-grid-line"></i></span>
                    Categories
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileDropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileDropdownOpen && (
                  <div className="pl-4 mt-1 space-y-0.5">
                    {categories.map((cat) => (
                      <a key={cat.id} href={cat.url} {...externalLinkAttrs} className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">
                        <span className="w-4 h-4 flex items-center justify-center"><i className={`${cat.icon} text-sm`}></i></span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <button
                  className={`flex items-center justify-between w-full px-3 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer ${navTextColor} ${isOnHero ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}
                  onClick={() => setMobileGuidesOpen(!mobileGuidesOpen)}
                >
                  <span className="flex items-center gap-2">
                    <span className="w-5 h-5 flex items-center justify-center"><i className="ri-book-open-line"></i></span>
                    Shopping Guides
                  </span>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuidesOpen ? 'rotate-180' : ''}`}></i>
                </button>
                {mobileGuidesOpen && (
                  <div className="pl-4 mt-1 space-y-0.5">
                    <Link to="/article/best-sugargoo-spreadsheet-2026" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Complete Guide 2026</Link>
                    <Link to="/article/how-to-use-sugargoo-spreadsheet" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">How To Use Spreadsheets</Link>
                    <Link to="/article/sugargoo-spreadsheet-shoes-guide" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Shoes Buying Guide</Link>
                    <Link to="/article/sugargoo-spreadsheet-budget-finds" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Budget Finds</Link>
                    <Link to="/article/sugargoo-shipping-guide-beginners" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">Shipping Guide</Link>
                    <Link to="/article/sugargoo-qc-guide-explained" onClick={() => setMobileMenuOpen(false)} className="block px-3 py-2.5 text-sm text-foreground-600 rounded-lg hover:bg-accent-100 hover:text-accent-800 transition-colors whitespace-nowrap">QC Guide</Link>
                  </div>
                )}
              </div>

              <a href={mainSiteUrl} {...externalLinkAttrs} className={`px-3 py-3 text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${navTextColor} ${isOnHero ? 'hover:bg-white/10' : 'hover:bg-background-100'}`}>Main Website</a>

              <a href={mainSiteUrl} {...externalLinkAttrs} className={`mx-3 mt-2 px-5 py-3 rounded-full text-sm font-semibold text-center transition-colors whitespace-nowrap ${isOnHero ? 'bg-white text-foreground-950' : 'bg-primary-500 text-background-50'}`}>Buy It Now</a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}